import React, { useState } from "react";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Add, EditOutlined } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import ShipmentRefundBasicDetail from "./ShipmentRefundComponents/ShipmentRefundBasicDetail";
import { useAppMeta, useAuth } from "hooks";
import {
  ShipmentRefundDetailContext,
  useShipmentRefundDetailContext,
} from "contexts/PaymentContexts/ShipmentRefundDetailContext";
import {
  useApproveShipmentRefundMutation,
  useGetShipmentRefundDetailQuery,
  useRejectShipmentRefundMutation,
} from "store/services/PaymentsApi/shipmentRefundApi";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import ShipmentRefundFinanceForm from "./ShipmentRefundComponents/ShipmentRefundFinanceForm";
import ShipmentRefundDetailTab from "./ShipmentRefundComponents/ShipmentRefundDetailTab";

const ShipmentRefundDetailScreen = () => {
  const { id } = useParams();

  const breadcrumbs = [
    {
      title: "Shipment Refunds",
      path: "/app/crm/shipment-refund/list",
    },
    { title: "Refund Detail" },
  ];

  const {
    data: shipmentRefundDetail,
    isLoading: loadingShipmentRefund,
    error,
  } = useGetShipmentRefundDetailQuery(id);

  useAppMeta("Shipment Refund Detail");

  return (
    <ShipmentRefundDetailContext.Provider
      value={{ shipmentRefundDetail, loadingShipmentRefund }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingShipmentRefund}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <ShipmentRefundBasicDetail />
          <Divider />
          <ShipmentRefundDetailTab />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </ShipmentRefundDetailContext.Provider>
  );
};

export default ShipmentRefundDetailScreen;

const PageActions = () => {

  const [openModal, setOpenModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const [openRejectDialog, setOpenRejectDialog] = useState(false);

  const [rejectRemarks, setRejectRemarks] = useState("");

  const { isSystemAdmin, isAccountAdmin, isEntityAdmin } = useAuth();

  const { shipmentRefundDetail, loadingShipmentRefund } =
    useShipmentRefundDetailContext();

  const [approveShipmentRefund, { isLoading: approvingRefund }] =
    useApproveShipmentRefundMutation();

  const [rejectShipmentRefund, { isLoading: rejectingRefund }] =
    useRejectShipmentRefundMutation();

  // handle approve shipment refund
  const handleApproveSubmit = async (payload) => {
    try {
      await approveShipmentRefund({
        _id: shipmentRefundDetail._id,
        ...payload,
      }).unwrap();
      toast.success("Shipment refund approved successfully");
      setOpenModal(false);
    } catch (error) {
      toast.error(error?.data?.message ?? "Failed to approve shipment refund");
    }
  };

  // handle reject shipment refund
  const handleRejectRefund = async () => {
    try {
      await rejectShipmentRefund({
        _id: shipmentRefundDetail._id,
        remarks: rejectRemarks,
      }).unwrap();
      toast.success("Shipment refund rejected successfully");
      setOpenRejectDialog(false);
      setRejectRemarks("");
      setAnchorEl(null);
    } catch (error) {
      toast.error(error?.data?.message ?? "Failed to reject shipment refund");
    }
  };

  const isReviewedOrRejected =
    shipmentRefundDetail?.financeReview?.status !== undefined;

  const collectButtonLabel = isReviewedOrRejected
    ? shipmentRefundDetail?.financeReview?.status === "Approved"
      ? "Approved"
      : "Rejected"
    : "Manage Refund";

  const isActionButtonDisabled =
    loadingShipmentRefund ||
    approvingRefund ||
    rejectingRefund ||
    isReviewedOrRejected;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleApproveClick = () => {
    setOpenModal(true);
    setAnchorEl(null);
  };

  const handleRejectClick = () => {
    setOpenRejectDialog(true);
    setAnchorEl(null);
  };

  // render page actions UI
  return (
    <>
      {isAccountAdmin && (
        <Box display="flex" gap={2} alignItems="center">
          {/* Edit Button — hidden once approved */}
          {shipmentRefundDetail?.refundStatus !== "Approved" && (
            <Link to={`/app/crm/shipment-refund/${shipmentRefundDetail?._id}/edit`}>
              <Button variant="contained" startIcon={<EditOutlined />}>
                Edit
              </Button>
            </Link>
          )}

          <Button
            startIcon={<Add fontSize="small" />}
            variant="contained"
            color="primary"
            onClick={handleMenuOpen}
            disabled={isActionButtonDisabled}
            sx={{
              borderRadius: "5px",
              textTransform: "none",
              fontSize: "1rem",
              px: 1.5,
              py: 0.5,
            }}
          >
            {collectButtonLabel}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 4,
              sx: {
                borderRadius: 2,
                minWidth: 160,
                mt: 1,
                p: 0.5,
              },
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={handleApproveClick}
              disabled={isReviewedOrRejected}
              sx={{ fontSize: "1rem", borderRadius: 1, minHeight: 36 }}
            >
              <Add fontSize="small" sx={{ mr: 1 }} />
              Approve
            </MenuItem>
            <MenuItem
              onClick={handleRejectClick}
              disabled={isReviewedOrRejected}
              sx={{ fontSize: "1rem", borderRadius: 1, minHeight: 36 }}
            >
              <EditOutlined fontSize="small" sx={{ mr: 1 }} />
              Reject
            </MenuItem>
          </Menu>
        </Box>
      )}

      {/* Approve Modal */}
      <BaseModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Approve Shipment Refund"
        width="800px"
      >
        <ShipmentRefundFinanceForm
          onSubmit={handleApproveSubmit}
          isBusy={approvingRefund}
          refundAmount={shipmentRefundDetail?.amount}
          shipmentRefundDetail={shipmentRefundDetail}
        />
      </BaseModal>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description"
      >
        <DialogTitle id="reject-dialog-title">Confirm Reject</DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-dialog-description">
            Are you sure you want to reject this shipment refund?
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            required
            label="Reject Reason"
            size="small"
            multiline
            rows={2}
            sx={{ mt: 2 }}
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenRejectDialog(false);
              setRejectRemarks("");
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectRefund}
            color="primary"
            autoFocus
            disabled={rejectingRefund || !rejectRemarks.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
